import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import BottomFixedContainer from '@/components/molecule/BottomFixedContainer';
import Button from '@/components/atom/Button';
import LucideIcon from '@/components/atom/Icons/LucideIcon';
import { BUTTON_CONST } from '@/src/constants/buttons';
import MainContentTitle from '@/components/molecule/MainContentTitle';
import DetailSection from '@/components/organism/DetailSection';
import { NAV_CONST } from '@/src/constants/nav';
import PATH from '@/src/routes/path';
import { getCommunityReportByIdAPI } from '@/src/apis/rest/communityReport';
import { DetailSctionRenderer } from '@/src/components/organism/DetailSection/renderer';
import { useState } from 'react';
import CommunityReportBlindModal from '@/src/pages/CommunityReport/components/CommunityReportBlindModal';
import MultipleImagePreview from '@/src/components/molecule/MultipleImagePreview';

const BACK_PATH = PATH.COMMUNITY_REPORT_LIST;

function CommunityReportDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams();

  // state
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);

  // query
  const { data: reportDetailData, refetch: refetchReportDetailData } = useQuery(
    {
      queryKey: ['community-posting-detail', id],
      queryFn: () => getCommunityReportByIdAPI({ id: Number(id) }),
      enabled: !!id,
      meta: {
        backUrl: BACK_PATH,
        callback: () => {
          navigate(BACK_PATH, { replace: true });
        },
      },
    },
  );

  // event
  const handleToggleModal = () => {
    setIsReportModalOpen(prev => !prev);
  };

  // render
  const renderBottomFixedContent = () => {
    return (
      <BottomFixedContainer>
        <div className="flex justify-between">
          <Button
            className="w-max"
            buttonSize="medium"
            buttonColor="outline"
            onClick={() => navigate(-1)}
          >
            <div className="flex items-center gap-x-2">
              <LucideIcon name="ChevronLeft" size={16} />
              {BUTTON_CONST.backToScreen}
            </div>
          </Button>
          {reportDetailData?.communityReportType === 'reception' && (
            <Button
              className="w-40 bg-red1"
              buttonSize="medium"
              buttonColor="red"
              onClick={() => {
                setIsReportModalOpen(true);
              }}
            >
              신고 접수 처리
            </Button>
          )}
        </div>
      </BottomFixedContainer>
    );
  };

  return (
    <>
      <MainContentTitle
        breadcrumbList={[
          NAV_CONST.communityReportManagement,
          NAV_CONST.communityReportDetail,
        ]}
        pageTitle={NAV_CONST.communityReportDetail}
      />
      <div className="flex flex-col gap-6">
        <DetailSection
          sectionTitle={
            <div className="flex w-full items-center justify-between">
              <span className="font-pretendardSemiBold text-b18sb">
                {reportDetailData?.communityType === 'posting'
                  ? '게시글 정보'
                  : '댓글 정보'}
              </span>
            </div>
          }
          data={reportDetailData}
          contents={[
            [
              {
                key: 'communityType',
                lable: '분류',
                render: DetailSctionRenderer.BADGE({
                  options: [
                    {
                      label: '댓글',
                      key: 'comment',
                      badgeColor: 'lightYellow',
                    },
                    {
                      label: '게시글',
                      key: 'posting',
                      badgeColor: 'lightBlue',
                    },
                  ],
                }),
              },
              {
                key: 'postedAt',
                lable: '작성 일시',
                valueSetter: data => {
                  return (
                    (data?.communityType === 'posting'
                      ? data?.communityPosting?.createdAt
                      : data?.communityComment?.createdAt) || '-'
                  );
                },
                render: DetailSctionRenderer.DATE({
                  format: 'YYYY-MM-DD HH:mm:ss',
                }),
              },
              {
                key: 'writerNick',
                lable: '작성자 닉네임',
                valueSetter: data => {
                  return (
                    (data?.communityType === 'posting'
                      ? data?.communityPosting?.customer?.nick
                      : data?.communityComment?.customer?.nick) || '-'
                  );
                },
              },
              {
                key: 'communityCategory',
                lable: '게시글 카테고리',
                valueSetter: data => {
                  return (
                    (data?.communityType === 'posting'
                      ? data?.communityPosting?.communityCategory?.categoryName
                      : data?.communityComment?.communityPosting
                          ?.communityCategory?.categoryName) || '-'
                  );
                },
              },
              {
                key: 'postingTitle',
                lable: '게시글 제목',
                valueSetter: data => {
                  return (
                    (data?.communityType === 'posting'
                      ? data?.communityPosting?.title
                      : data?.communityComment?.communityPosting?.title) || '-'
                  );
                },
              },
            ],
            [
              {
                key: 'postingContent',
                lable: '게시글 내용',
                valueSetter: data => {
                  return (
                    (data?.communityType === 'posting'
                      ? data?.communityPosting?.content
                      : data?.communityComment?.communityPosting?.content) ||
                    '-'
                  );
                },
              },
              {
                key: 'postingMedias',
                lable: '게시글 사진',
                valueSetter: data => {
                  if (data?.communityType === 'posting') {
                    return data?.communityPosting?.communityMedias
                      ? JSON.stringify(data?.communityPosting?.communityMedias)
                      : '';
                  } else {
                    return data?.communityComment?.communityPosting
                      ?.communityMedias
                      ? JSON.stringify(
                          data?.communityComment?.communityPosting
                            ?.communityMedias,
                        )
                      : '';
                  }
                },
                render: value => {
                  const result = JSON.parse(value || '[]');

                  if (Array.isArray(result) && result.length < 1) return '-';

                  return (
                    <MultipleImagePreview
                      images={result}
                      width={128}
                      height={128}
                    />
                  );
                },
              },
              ...(reportDetailData?.communityType === 'comment'
                ? [
                    {
                      key: ['communityComment', 'content'],
                      lable: '댓글 내용',
                    },
                  ]
                : []),
            ],
          ]}
        />
        <DetailSection
          sectionTitle={
            <div className="flex w-full items-center justify-between">
              <span className="font-pretendardSemiBold text-b18sb">
                신고 접수 정보
              </span>
            </div>
          }
          data={reportDetailData}
          contents={[
            [
              {
                key: 'communityReportType',
                lable: '신고 상태',
                render: DetailSctionRenderer.BADGE({
                  options: [
                    {
                      label: '신고 접수',
                      key: 'reception',
                      badgeColor: 'lightYellow',
                    },
                    { label: '블라인드', key: 'blind', badgeColor: 'lightRed' },
                    {
                      label: '신고 반려',
                      key: 'common',
                      badgeColor: 'lightGray',
                    },
                  ],
                }),
              },
              {
                key: ['customer', 'nick'],
                lable: '신고자 닉네임',
              },
            ],
            [
              {
                key: 'createdAt',
                lable: '신고 접수 일시',
                render: DetailSctionRenderer.DATE({
                  format: 'YYYY-MM-DD HH:mm:ss',
                }),
              },
              {
                key: 'reportReason',
                lable: '신고 사유',
                valueSetter: data => {
                  return (
                    (data?.communityReportReasonType === 'etc'
                      ? `${data?.communityReportReasonTypeKr}(${data?.reportReason})`
                      : data?.communityReportReasonTypeKr) || '-'
                  );
                },
              },
            ],
          ]}
        />
        <DetailSection
          className="mb-[100px]"
          sectionTitle={
            <div className="flex w-full items-center justify-between">
              <span className="font-pretendardSemiBold text-b18sb">
                신고 처리 정보
              </span>
            </div>
          }
          data={reportDetailData}
          contents={[
            [
              {
                key: 'communityReportType',
                lable: '처리 상태',
                render: DetailSctionRenderer.BADGE({
                  options: [
                    {
                      label: '처리중',
                      key: 'reception',
                      badgeColor: 'lightYellow',
                    },
                    {
                      label: '처리 완료',
                      key: 'blind',
                      badgeColor: 'lightGray',
                    },
                    {
                      label: '처리 완료',
                      key: 'common',
                      badgeColor: 'lightGray',
                    },
                  ],
                }),
              },
              {
                key: ['administrator', 'name'],
                lable: '처리 담당자',
              },
            ],
            [
              {
                key: 'reportHandledAt',
                lable: '처리 일시',
                render: DetailSctionRenderer.DATE({
                  format: 'YYYY-MM-DD HH:mm:ss',
                }),
              },
              {
                key: 'reportHandlingReason',
                lable: '처리 사유',
              },
            ],
          ]}
        />
      </div>
      {renderBottomFixedContent()}

      {/* 모달 */}
      {reportDetailData && isReportModalOpen && (
        <CommunityReportBlindModal
          open={isReportModalOpen}
          onOpenChange={() => handleToggleModal()}
          onSubmit={() => {
            refetchReportDetailData();
          }}
          data={reportDetailData}
        />
      )}
    </>
  );
}

export default CommunityReportDetailPage;
